import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Group, Loader, Text } from '@mantine/core';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { UserForm } from '../components/EmployeeForm.tsx';
import { PageHeader } from '../components/app/index.ts';
import { employeeAPI } from '../services/api.ts';
import { toast } from '../lib/toast.ts';

// Kept outside the component so the form does not see a new object each render.
const NEW_USER = {};

/**
 * The add and edit screens for a user: `/employees/new` and
 * `/employees/:id/edit`.
 */
export const EmployeeEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    employeeAPI
      .details(Number(id))
      .then((response) => {
        if (!cancelled) setEmployee(response.data);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(
            err.response?.data?.message || err.message || 'Failed to load user'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const back = editing ? `/employees/${id}` : '/employees';

  const handleSubmit = async (values: any) => {
    try {
      const response = values.is_edit_mode
        ? await employeeAPI.update(values.id, values)
        : await employeeAPI.create(values);

      if (response.success) {
        // A new user's profile, when the server says which one it is.
        const savedId = editing ? id : response.data?.id || values.id;
        navigate(savedId ? `/employees/${savedId}` : '/employees');
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || err.message || 'Operation failed'
      );
    }
  };

  return (
    <Layout>
      <Box maw={960} mx="auto">
        <PageHeader
          back={back}
          title={editing ? 'Edit user' : 'Add user'}
          description={
            editing
              ? employee?.name
              : 'Create a sign-in for an employee and set where they work.'
          }
        />

        {loading ? (
          <Group justify="center" h={240}>
            <Loader />
          </Group>
        ) : error ? (
          <Alert color="red" variant="light">
            <Group justify="space-between" gap="sm">
              <Text size="sm">{error}</Text>
              <Button
                size="compact-sm"
                variant="subtle"
                onClick={() => navigate('/employees')}
              >
                Back to users
              </Button>
            </Group>
          </Alert>
        ) : (
          <UserForm
            key={employee?.id ?? 'new'}
            initialValues={employee || NEW_USER}
            onSubmit={handleSubmit}
            onCancel={() => navigate(back)}
          />
        )}
      </Box>
    </Layout>
  );
};

export default EmployeeEdit;
